if (!customElements.get('grouped-slideshow')) {
  customElements.define('grouped-slideshow', class extends HTMLElement {
    connectedCallback() {
      // Defer until the section's sibling text and navigation layers exist.
      this.frame = requestAnimationFrame(() => this.init());
    }

    init() {
      if (!this.isConnected || this.slider) return;
      this.items = Array.from(this.children);
      if (!this.items.length) return;
      this.section = this.closest('.shopify-section');
      this.layers = Array.from(this.section.querySelectorAll('[data-slideshow-words]'));
      this.dots = Array.from(this.section.querySelectorAll('.flickity-page-dot'));
      this.desktop = window.matchMedia('(min-width: 1024px)');
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        groupCells: this.desktop.matches,
        autoPlay: this.hasAttribute('autoplay')
          ? Number(this.getAttribute('autoplay-speed')) * 1000 : false,
      });
      this.slider.on('change', () => this.updateSelection());
      this.addEventListener('slider:previous', () => this.slider.previous(), options);
      this.addEventListener('slider:next', () => this.slider.next(), options);
      this.addEventListener('slider:play', () => this.slider.playPlayer(), options);
      this.addEventListener('slider:pause', () => this.slider.pausePlayer(), options);
      this.desktop.addEventListener('change', () => {
        const selected = this.slider.selectedElements[0];
        this.slider.options.groupCells = this.desktop.matches;
        // resize() skips updates during a transition; rebuild cells even then.
        this.slider.getSize();
        this.slider.reloadCells();
        this.slider.selectCell(selected, false, true);
        this.updateSelection();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const item = this.items.find((item) => item === event.target || item.contains(event.target));
        if (item) this.slider.selectCell(item);
        this.slider.stopPlayer();
      }, options);
      this.updateSelection();
    }

    select(index) {
      this.slider?.select(index);
    }

    updateSelection() {
      const selected = new Set(this.slider.selectedElements);
      const previous = this.selectedItems || new Set();
      this.selectedItems = selected;
      let offset = 0;

      this.items.forEach((item, index) => {
        const active = selected.has(item);
        const units = this.desktop.matches ? Number(item.dataset.slideUnits) || 6 : 6;
        // Flickity already hides inactive media from assistive technology.
        item.inert = !active;
        this.layers.forEach((layer) => {
          const content = layer.children[index];
          if (!content) return;
          content.setAttribute('aria-current', String(active));
          content.inert = !active;
          content.style.setProperty('--slide-units', units);
          content.style.setProperty('--slide-offset', offset);
        });
        if (active) offset += units;

        if (active && !previous.has(item)) {
          item.querySelectorAll('animate-element').forEach((animation) => animation.refresh());
        }
        if (item.dataset.type === 'video') {
          // Pause all variants before starting the currently visible one.
          item.querySelectorAll('video-media').forEach((video) => video.pause());
          if (active) theme.utils.visibleMedia(item.querySelectorAll('video-media'))?.play();
        }
      });

      const pageCount = this.slider.slides.length;
      this.dots.forEach((dot, index) => {
        dot.hidden = index >= pageCount;
        dot.setAttribute('aria-current', String(index === this.slider.selectedIndex));
      });
      this.section.querySelectorAll('.slideshow-dots, .slideshow-mobile-arrows').forEach((navigation) => {
        navigation.hidden = pageCount <= 1;
      });
      this.dispatchEvent(new CustomEvent('slider:change', {
        bubbles: true,
        detail: { currentPage: this.slider.selectedIndex },
      }));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abortController?.abort();
      this.slider?.destroy();
      this.slider = null;
      this.selectedItems = null;
    }
  });
}
